import React from 'react';
import _get from 'lodash.get';
import { isApp } from 'utils/getParams';
import { loadLayoutDataList } from 'reduxUtils/modules_v2/layout';
import { getSiteConfig } from 'reduxUtils/modules/common';
import Layout from 'components_v2/desktop/Layout';
import { fireGA4Script } from 'utils/common';
import { commonHeadScripts } from 'helpers/scripts/headScripts';
import { loadPageAdCode } from '../../../../helpers/ads/revenueRedux';
import {
  // loadGamesListingData,
  loadMetaWidgetData,
  loadPuzzleData,
  loadGamesConfigData,
  loadFactCheckerData,
} from '../../redux';
import { getGameComponent, getGamePageValidity } from '../utils';
import { TOI_LIVE_DOMAIN } from '../../../../constants';

const IframeWrapper = ({ children }) => (
  <div style={{ height: '100vh', width: '100%', overflow: 'auto' }}>
    {children}
  </div>
);

async function action(context) {
  const dataPromiseArray = [];
  const localParams = { 0: context.path };
  let state = context.store.getState();
  const gameType = context.params?.gamename?.toLowerCase() || '';
  const gameid = context.params?.gameid?.toLowerCase() || '';
  const isPrime = !!state.isPrime;
  const isAppView = isApp(context.query);
  const isServer = typeof window === 'undefined';
  const showPreload = gameType === 'factchecker';
  const imageInfo =
    gameType === 'factchecker'
      ? [
          'https://static.toiimg.com/photo/114306702.cms',
          'https://static.toiimg.com/photo/114306692.cms',
          //'https://static.toiimg.com/photo/114208586.cms',
        ]
      : [];
  const isLGGamePlayScreen =
    gameType === 'location-guesser' && (gameid !== 'practice' && gameid !== '');
  // Get URL parameters
  const isGameIframe = context.query?.isiframe === 'true';
  const iframeSource = context.query?.source;
  const iframeLangSource = context.query?.gamesrc;
  const iframeLang = context.query?.lang;
  const isIframeMWeb = context.query?.ismweb === 'true';

  const newParams = {
    ...localParams,
    fullPath: `/games${gameType ? `/${gameType}` : ''}`,
    isWapView: false,
    gameType,
    gameid,
    isAppView,
    isPrime,
    isGameIframe,
    iframeSource,
    iframeLang,
    isIframeMWeb,
    iframeLangSource,
  };

  // if (!state.toigames.gameslisting) {
  //   dataPromiseArray.push(
  //     context.store.dispatch(loadGamesListingData(newParams)),
  //   );
  // }
  if (!state.toigames.gamesmetawidget) {
    dataPromiseArray.push(
      context.store.dispatch(loadMetaWidgetData(newParams)),
    );
  }

  if (!state.toigames.puzzledata && gameType && gameType !== 'factchecker') {
    dataPromiseArray.push(context.store.dispatch(loadPuzzleData(newParams)));
  }

  if (!state.toigames.puzzledata && gameType === 'factchecker') {
    dataPromiseArray.push(
      context.store.dispatch(loadFactCheckerData(newParams)),
    );
  }

  if (!state.header.data || !state.footer_v2.data) {
    dataPromiseArray.push(
      context.store.dispatch(loadLayoutDataList(newParams)),
    );
  }
  if (!state.toigames.gamesconfig) {
    dataPromiseArray.push(
      context.store.dispatch(loadGamesConfigData(newParams)),
    );
  }

  if (typeof window === 'undefined') {
    dataPromiseArray.push(context.store.dispatch(getSiteConfig()));
  }

  if (!isPrime && typeof window === 'undefined') {
    const pagename = 'games';
    const platform = 'web';
    const sectionl1 = gameType || gameid ? 'showpage' : '';
    const jpiconfig = {
      Tmpl_SCN: pagename,
      SCN: sectionl1,
      platform,
      Channel: 'toi',
    };
    dataPromiseArray.push(context.store.dispatch(loadPageAdCode(jpiconfig)));
  }

  // wait for responses on server side
  if (typeof window === 'undefined') {
    await Promise.all(dataPromiseArray);
  }

  // get updated state
  state = context.store.getState();
  let status = 200;
  const title = 'Get Latest News Updates from Times Of India';

  const isValidPage = getGamePageValidity(gameType, gameid);
  let dataAvailable = !!state.toigames.gamesmetawidget && isValidPage;
  // To rule out invalid game IDs
  if (
    gameType !== 'factchecker' &&
    dataAvailable &&
    gameid &&
    gameid !== 'practice' &&
    !state.toigames.puzzledata
  ) {
    dataAvailable = false;
  }
  if (gameType === 'factchecker' && !state.toigames.factcheckerdata) {
    dataAvailable = false;
  }
  // !!state.toigames.gameslisting && !!state.toigames.gamesmetawidget;

  if (isServer && !dataAvailable) {
    status = 404;
  }

  const getSeoData = async () => {
    const metaInfo = _get(state, 'toigames.gamesmetawidget.metaInfo', {});
    const seoObj = metaInfo
      ? {
          description: metaInfo?.Altdescription?.value,
          title: metaInfo?.Alttitle?.value,
          keywords: metaInfo?.MetaKeywords?.value,
          canonical:
            metaInfo?.canonicalURL?.value ||
            `${TOI_LIVE_DOMAIN}${context.pathname}`,
        }
      : {};
    //seoObj.noindexnofollow = true;
    return seoObj;
  };

  let dynamicChunksToLoad = [];

  const getComponent = async pageTitle => {
    if (typeof window === 'undefined' && !dataAvailable) {
      return (
        <div>
          Something went wrong. Click{' '}
          <a href="https://timesofindia.indiatimes.com">here</a> to browse TOI
        </div>
      );
    }

    const gameComponentData = await getGameComponent(gameType, gameid);

    const {
      componentToRender,
      isGamesPracticePage,
      isGameplayPage,
      dynamicChunks,
    } = gameComponentData;

    // Only clone and add props if isGameIframe is true
    const finalComponent = isGameIframe
      ? React.cloneElement(componentToRender, {
          isGameIframe,
          isIframeMWeb,
          iframeSource,
          iframeLang,
          iframeLangSource,
        })
      : componentToRender;

    dynamicChunksToLoad = dynamicChunks;

    if (typeof window !== 'undefined') {
      fireGA4Script({ params: { isGameplayPage, isGamesPracticePage } });
      if (document) {
        document.body.classList.add(`game_page`);
      }
    }

    // If isGameIframe=true, return just the component wrapped in IframeWrapper
    if (isGameIframe) {
      return <IframeWrapper>{finalComponent}</IframeWrapper>;
    }

    // Otherwise return the normal Layout
    return (
      <Layout
        layoutClass="layout_type_2"
        title={pageTitle}
        params={newParams}
        query={Object.keys(context.query).length ? context.query : {}}
        layoutType=""
        hideStickyBackToTop
        rhs={false}
        adPosition="masthead"
        showAdBlockerInINEU
        showTimesTop10
        hideAdBlockerDetector
        refreshFBNAd
        hideBreadcrumAtBottom
        pageType="Games" //-if change then nav gets brekon
        pageTypeWithVersion="TOI Games"
        hideAdCude
        navigation={state.header.data && state.header.data.navigation}
        hideAffiliatesFloatingNudge
        templateName="TOI Games"
        hideAds
        isPrime
        skipReloadForPrimeLogin
      >
        {finalComponent}
      </Layout>
    );
  };

  const component = await getComponent(title);

  const gameMapCommonHeadScripts = isLGGamePlayScreen
    ? [
        <script
          src="https://cdn.maptiler.com/maptiler-sdk-js/v2.3.0/maptiler-sdk.umd.min.js"
          type="text/javascript"
        />,
        <script
          src="https://cdn.maptiler.com/maptiler-geocoding-control/v1.3.3/maptilersdk.umd.js"
          type="text/javascript"
        />,
        <script
          src="https://tvid.in/sdk/slikeloader.js"
          type="text/javascript"
          id="tvid_sdk_js"
        />,
        <script
          src="https://timesofindia.indiatimes.com/video_comscore_api/version-3.cms"
          type="text/javascript"
          id="video_comscore_js"
        />,
        <script
          src="https://tvid.in/r/spl.toiweba5ec9705eb7ac2c984033e061.js"
          type="text/javascript"
          id="light_tvid_sdk_js"
        />,
      ]
    : [];
  const externalStyles = isLGGamePlayScreen
    ? [
        'https://cdn.maptiler.com/maptiler-sdk-js/v2.3.0/maptiler-sdk.css',
        'https://cdn.maptiler.com/maptiler-geocoding-control/v1.3.3/style.css',
      ]
    : [];

  return {
    title,
    maxAge: 21600,
    chunks: ['toi_gamesdesktop', ...dynamicChunksToLoad],
    seoData: await getSeoData(),
    status,
    component,
    skipRouteAds: true,
    isPrime: true,
    showPreload,
    imageInfo,
    headTags: [...commonHeadScripts, ...gameMapCommonHeadScripts],
    externalStyles,
  };
}

export default action;
